// کیف پول — پارسه, and only پارسه.
//
// ── One of THREE kinds of money, and the other two are unreachable ─────────
//
//   wallet       پارسه / Parsina Coin — platform usage credit. THIS ONE.
//                `owner_type: "student"`, ledger unit TOMAN/PC.
//   accounting   patient ↔ clinic money, in WHOLE RIALS. Not reachable here.
//   subscription what a clinic pays Parsina. Not reachable here.
//
// `accounting`'s helpers divide by ten because its numbers are rials; this one's
// are not. Never carry a helper between them (CORE.md §8, hard rule 14). That is
// why nothing in this file imports `money.*` from the kit.
//
// ── There is no starter-grant mutation, and there must not be one ─────────
//
// The wallet AUTO-CREATES a wallet on its first balance read with
// `free_balance = FREE_MONTHLY_CREDITS = 500.0`, and refills it to 500 at each
// calendar-month rollover. So reading `myWalletBalance` IS the starter grant, and
// it renews itself. A `grantStarterCredit` mutation plus a "has been granted"
// flag would be a second source of truth that can disagree with the wallet, and
// would double-grant on any retry of a call whose response was lost.
//
// The balance itself lives in ./useMe.js — one copy, shared with the rail and the
// assistant — so this hook owns the transactions, the gift codes and the two
// actions, and reports a moved balance back to that store.

import React from 'react';

import { bffQuery, bffMutation, errorCode } from '../lib/bffClient.js';
import {
  WALLET_TRANSACTIONS_QUERY,
  MY_GIFT_CODES_QUERY,
  GIFT_CODE_PREVIEW_QUERY,
  REDEEM_GIFT_CODE_MUTATION,
  TOP_UP_WALLET_MUTATION,
} from '../graphql/documents.js';
import { DEFAULT_PAGE_SIZE, offsetFor } from '../lib/paging.js';
import { PAYMENT_GATEWAY, toGatewayAmount } from '../constants/gateway.js';
import { refreshBalance, setBalance } from './useMe.js';

/** `credit` | `debit`, or null for both. Filtered SERVER-side — see the note below. */
export const TX_FILTERS = [null, 'credit', 'debit'];

/**
 * The transaction ledger.
 *
 * `type` is filtered on the SERVER even though the rows are already here: the
 * frozen schema's `totalCount` is "the size of the whole FILTERED set, not of
 * this page", so a client-side filter would page against a total that counts
 * rows it is hiding — the pager would offer pages that render empty.
 */
export function useWalletTransactions({ page = 1, pageSize = DEFAULT_PAGE_SIZE, type = null } = {}) {
  const [transactions, setTransactions] = React.useState([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);

    bffQuery(WALLET_TRANSACTIONS_QUERY, {
      limit: pageSize,
      offset: offsetFor(page, pageSize),
      type: type || null,
    })
      .then((data) => {
        if (!alive) return;
        setTransactions(data?.myWalletTransactions?.transactions || []);
        setTotalCount(data?.myWalletTransactions?.totalCount || 0);
        setError(null);
      })
      .catch((err) => {
        if (!alive) return;
        console.error('[useWalletTransactions] failed:', errorCode(err) || err?.message);
        setError(err);
        setTransactions([]);
        setTotalCount(0);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [page, pageSize, type, nonce]);

  return {
    transactions,
    totalCount,
    loading,
    error,
    reload: () => setNonce((n) => n + 1),
  };
}

/**
 * کدهای هدیه — the codes THIS student has redeemed, plus preview and redeem.
 *
 * `myGiftCodes` is scoped to the caller's own token inside the resolver; there is
 * no argument for whose codes to list, here or in the contract.
 */
export function useGiftCodes() {
  const [codes, setCodes] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [nonce, setNonce] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    bffQuery(MY_GIFT_CODES_QUERY)
      .then((data) => {
        if (!alive) return;
        setCodes(data?.myGiftCodes || []);
        setError(null);
      })
      .catch((err) => {
        if (!alive) return;
        console.error('[useGiftCodes] failed:', errorCode(err) || err?.message);
        setError(err);
        setCodes([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [nonce]);

  const reload = React.useCallback(() => setNonce((n) => n + 1), []);

  /**
   * Check a code WITHOUT spending it.
   *
   * Returns null for an unknown code rather than throwing, because the schema
   * answers null rather than erroring: a typo belongs in the field as
   * «کد معتبر نیست», not in an error banner over the whole screen.
   */
  const preview = React.useCallback(async (code) => {
    const value = String(code || '').trim();
    if (!value) return null;
    const data = await bffQuery(GIFT_CODE_PREVIEW_QUERY, { code: value });
    return data?.giftCodePreview || null;
  }, []);

  /**
   * Spend a code and receive its پارسه.
   *
   * The BFF marks the code spent in `marketing` FIRST and only then asks the
   * wallet to grant — mark-then-grant under-delivers if it breaks (recoverable,
   * and the resolver releases the code back), while grant-then-mark
   * OVER-delivers: پارسه out the door with the code still live. That order is the
   * design and it lives on the server; nothing this client does can change it,
   * and nothing here should retry a redemption on the student's behalf.
   *
   * The result carries the balance AFTER the grant landed, so it is adopted
   * directly rather than triggering another read that could race an AI spend and
   * make the redemption look smaller than it was.
   */
  const redeem = React.useCallback(
    async (code) => {
      const data = await bffMutation(REDEEM_GIFT_CODE_MUTATION, {
        code: String(code || '').trim(),
      });
      const result = data?.redeemGiftCode || null;
      if (result?.balance?.balance != null) setBalance(result.balance.balance);
      reload();
      return result;
    },
    [reload],
  );

  return { codes, loading, error, reload, preview, redeem };
}

/**
 * شارژ پارسه — begin a top-up and get the URL to send the browser to.
 *
 * `amountToman` is what the UI prices in. `topUpWallet(amount)` takes THE
 * GATEWAY'S OWN WIRE UNIT, and the two gateways disagree: Zarinpal settles in
 * Toman, Sepehr in Rials. Sending a Toman figure to Sepehr undercharges by 10×
 * and the payment SUCCEEDS — nothing errors and the student is credited, so the
 * difference is only visible in a reconciliation nobody runs. The conversion
 * happens once, in src/constants/gateway.js, and is applied here at the moment
 * the amount is handed to the BFF.
 *
 * KNOWN SERVER PLACEHOLDER, not fixable from this client: the top-up path
 * credits 1 Toman = 1 پارسه with no conversion, while the price list puts a
 * پارسه at ~1,000 Toman. So the screen prints what it CHARGES and then shows the
 * balance the wallet reports after the callback settles — never an arithmetic
 * guess at what will be credited. See src/constants/pricing.js.
 */
export function useTopUp() {
  const [starting, setStarting] = React.useState(false);

  const start = React.useCallback(async ({ amountToman, description, returnUrl }) => {
    setStarting(true);
    try {
      const data = await bffMutation(TOP_UP_WALLET_MUTATION, {
        gateway: PAYMENT_GATEWAY,
        amount: toGatewayAmount(amountToman, PAYMENT_GATEWAY),
        description: description || null,
        // Where the BROWSER lands after paying. Where the GATEWAY posts its
        // result is not a client input — that endpoint has to parse a bank's
        // POST and settle the wallet, and it is the wallet's own callback.
        returnUrl: returnUrl || `${window.location.origin}/credits`,
      });
      return data?.topUpWallet || null;
    } finally {
      setStarting(false);
    }
  }, []);

  return { start, starting };
}

/**
 * The composite the credits screen mounts: transactions + gift codes + top-up,
 * with the balance served from the shared identity store.
 */
export function useWallet({ page = 1, pageSize = DEFAULT_PAGE_SIZE, type = null } = {}) {
  const ledger = useWalletTransactions({ page, pageSize, type });
  const gifts = useGiftCodes();
  const topUp = useTopUp();

  return {
    ledger,
    gifts,
    topUp,
    // Re-read after returning from the bank, where the credit landed through the
    // wallet's own HTTP callback and nothing in this tab was told about it.
    refreshBalance,
  };
}

export default useWallet;
